import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { zar } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

export default function SalesCount({ embedded = false }) {
  const [day, setDay] = useState(format(new Date(), "yyyy-MM-dd"));
  const [menuItems, setMenuItems] = useState([]);
  const [qtys, setQtys] = useState({});
  const [existing, setExisting] = useState(null);
  const [result, setResult] = useState(null);
  const [recorded, setRecorded] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/recipes?type=dish&status=active").then((r) => setMenuItems(r.data)).catch(() => toast.error("Failed to load menu items"));
  }, []);

  useEffect(() => {
    setResult(null);
    api.get(`/sales-counts/${day}`).then((r) => {
      const lines = r.data.lines || [];
      setExisting(lines.length ? r.data : null);
      setRecorded(r.data.recorded_revenue || 0);
      const q = {};
      lines.forEach((l) => { q[l.recipe_id] = l.quantity_sold; });
      setQtys(q);
    }).catch(() => {});
  }, [day]);

  const expected = menuItems.reduce((a, m) => a + (Number(qtys[m.id]) || 0) * (m.selling_price || 0), 0);
  const variance = expected - recorded;

  const submit = async () => {
    const lines = Object.entries(qtys)
      .filter(([, q]) => Number(q) > 0)
      .map(([recipe_id, q]) => ({ recipe_id, quantity_sold: Number(q) }));
    if (!lines.length) return toast.error("Enter at least one quantity");
    setBusy(true);
    try {
      const { data } = await api.post("/sales-counts", { date: day, lines });
      setResult(data);
      toast.success(data.replaced ? "Sales count replaced — stock re-depleted" : "Sales count posted — stock depleted");
    } catch (e) { toast.error(apiError(e, "Submit failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="sales-count-page">
      {!embedded && (
        <div>
          <h1 className="font-heading text-3xl font-bold">Daily Sales Count</h1>
          <p className="text-muted-foreground mt-1">Quantities sold per menu item — depletes stock through the recipe tree on submit</p>
        </div>
      )}
      <div className="max-w-xs space-y-1.5">
        <Label>Trading day</Label>
        <Input type="date" data-testid="sales-date" max={format(new Date(), "yyyy-MM-dd")} value={day} onChange={(e) => setDay(e.target.value)} />
        {existing && <p className="text-xs text-[#D99A29]" data-testid="sales-existing-note">A count for this date exists — submitting replaces it and reverses the old stock movements.</p>}
      </div>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]" data-testid="sales-items-table">
          <thead><tr className="border-b text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Menu item</th>
            <th className="px-4 py-3 font-medium text-right">Cost / portion</th>
            <th className="px-4 py-3 font-medium text-right">Selling price</th>
            <th className="px-4 py-3 font-medium text-right w-32">Qty sold</th>
          </tr></thead>
          <tbody>
            {menuItems.map((m) => (
              <tr key={m.id} className="border-b last:border-0" data-testid={`sales-item-${m.id}`}>
                <td className="px-4 py-2.5 font-medium">{m.name}</td>
                <td className="px-4 py-2.5 num text-right">{m.cost_per_yield_unit != null ? zar(m.cost_per_yield_unit) : "—"}</td>
                <td className="px-4 py-2.5 num text-right">{m.selling_price != null ? zar(m.selling_price) : "—"}</td>
                <td className="px-4 py-2.5"><Input type="number" min="0" step="any" className="w-24 ml-auto text-right" data-testid={`sales-qty-${m.id}`} value={qtys[m.id] ?? ""} onChange={(e) => setQtys({ ...qtys, [m.id]: e.target.value })} /></td>
              </tr>
            ))}
            {menuItems.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No active menu-item recipes yet — add them on the Recipes screen.</td></tr>}
          </tbody>
        </table>
      </div>
      {(expected > 0 || recorded > 0) && (
        <div className="glass border border-border rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="sales-crosscheck">
          <span>Expected from item counts: <strong className="num" data-testid="crosscheck-expected">{zar(expected)}</strong></span>
          <span>Recorded revenue (Revenue → Input): <strong className="num" data-testid="crosscheck-recorded">{zar(recorded)}</strong></span>
          <span data-testid="crosscheck-variance"
            className={`font-medium ${Math.abs(variance) < 0.01 ? "text-[#A9C97E]" : "text-[#F87171]"}`}>
            {Math.abs(variance) < 0.01 ? "✓ matches" : `Variance: ${variance > 0 ? "+" : "−"}${zar(Math.abs(variance))}${recorded > 0 ? ` (${Math.round(Math.abs(variance) / recorded * 100)}%)` : ""}`}
          </span>
        </div>
      )}
      <Button onClick={submit} disabled={busy || menuItems.length === 0} data-testid="sales-submit-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
        {busy ? "Posting…" : existing ? "Replace count & re-deplete stock" : "Submit count & deplete stock"}
      </Button>

      {result && (
        <Card className="border-border shadow-none" data-testid="sales-result">
          <CardContent className="pt-5 space-y-3">
            <p className="font-heading font-semibold flex items-center gap-2"><CheckCircle size={18} className="text-[#A9C97E]" /> Posted for {result.date}{result.replaced && " (replaced previous count)"}</p>
            <p className="text-sm text-muted-foreground">{result.movements_created} stock movement(s) created</p>
            {result.usage.length > 0 && (
              <div className="space-y-1">
                {result.usage.map((u, i) => (
                  <div key={u.stock_item_id || u.stock_item_name || i} className="flex justify-between text-sm border-b border-border last:border-0 pb-1" data-testid={`sales-usage-${i}`}>
                    <span>{u.stock_item_name}</span>
                    <span className="num">−{u.quantity} base units · {zar(u.value)}</span>
                  </div>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="bg-[#F87171]/10 text-[#F87171] rounded-md p-3 text-sm space-y-1" data-testid="sales-skipped">
                <p className="font-medium flex items-center gap-1.5"><Warning size={14} /> {result.skipped.length} item(s) had no recipe defined and were not depleted:</p>
                {result.skipped.map((s, i) => <p key={s.recipe_id || i} className="text-xs">{s.recipe_id} — {s.reason}</p>)}
              </div>
            )}
            {(result.warnings || []).length > 0 && (
              <div className="bg-[#D99A29]/10 text-[#8a6414] rounded-md p-3 text-sm space-y-1" data-testid="sales-warnings">
                <p className="font-medium flex items-center gap-1.5"><Warning size={14} /> Unit conversion warnings:</p>
                {result.warnings.map((w) => <p key={w} className="text-xs">{w}</p>)}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
